import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Search, Copy, RotateCcw, GitBranch, CheckCircle2, AlertTriangle, Clock, Code, FileText, User, Bot } from 'lucide-react';
import { filterSections, highlightText as highlightTextUtil, type TimelineSection } from './timelineUtils';

const DEFAULT_MOCK_SECTIONS: TimelineSection[] = [{
  id: '1',
  timestamp: '2:34 PM',
  userPrompt: 'Implement caching for the DNS lookup service',
  agentResponses: [{
    id: 'r1',
    content: "I'll implement a Redis-based caching layer for the DNS lookup service. This will reduce latency by 80% for repeated queries.",
    model: 'Claude 3.5 Sonnet',
    toolUse: [{
      name: 'code_search',
      description: 'Searched for existing DNS implementation'
    }, {
      name: 'write',
      description: 'Created cache.ts with Redis client'
    }],
    artifacts: [{
      name: 'cache.ts',
      type: 'code'
    }, {
      name: 'redis-config.json',
      type: 'config'
    }]
  }],
  status: 'success',
  hasCode: true,
  hasArtifacts: true
}, {
  id: '2',
  timestamp: '2:45 PM',
  userPrompt: 'Add error handling for cache misses',
  agentResponses: [{
    id: 'r2',
    content: 'Added comprehensive error handling with fallback to direct DNS lookup when cache is unavailable.',
    model: 'Claude 3.5 Sonnet',
    toolUse: [{
      name: 'edit',
      description: 'Updated cache.ts with error handling'
    }]
  }],
  status: 'success',
  hasCode: true
}, {
  id: '3',
  timestamp: '3:12 PM',
  userPrompt: 'Run tests to verify the implementation',
  agentResponses: [{
    id: 'r3',
    content: 'Tests failed due to missing Redis connection configuration.',
    model: 'GPT-4o'
  }],
  status: 'error'
}];

interface TimelineViewProps {
  sections: TimelineSection[];
  onExpand?: (id: string) => void;
}
export function TimelineView({
  sections: initialSections,
  onExpand
}: TimelineViewProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'code' | 'errors' | 'artifacts'>('all');
  const [compactMode, setCompactMode] = useState(false);
  const toggleSection = (id: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSections(newExpanded);
    if (onExpand) onExpand(id);
  };
  const expandAll = () => {
    setExpandedSections(new Set(initialSections.map(s => s.id)));
  };
  const collapseAll = () => {
    setExpandedSections(new Set());
  };
  // Mock sections for demo
  const sections: TimelineSection[] = useMemo(
    () => initialSections.length > 0 ? initialSections : DEFAULT_MOCK_SECTIONS,
    [initialSections]
  );

  // Filter sections based on search and activeFilter
  const filteredSections = useMemo(() =>
    filterSections(sections, searchQuery, activeFilter),
    [sections, searchQuery, activeFilter]
  );

  // Highlight matching text in search results
  const highlightText = (text: string) => highlightTextUtil(text, searchQuery);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="w-3 h-3 text-success-400" />;
      case 'error':
        return <AlertTriangle className="w-3 h-3 text-danger-400" />;
      case 'pending':
        return <Clock className="w-3 h-3 text-warning-400 animate-pulse" />;
      default:
        return null;
    }
  };
  return <div className="flex flex-col h-full bg-surface-900">
      {/* Controls */}
      <div className="p-4 border-b border-surface-600 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">Timeline</h3>
          <div className="flex items-center gap-2">
            <button onClick={compactMode ? () => setCompactMode(false) : () => setCompactMode(true)} className="text-xs text-text-secondary hover:text-text-primary px-2 py-1 rounded hover:bg-surface-800 transition-colors">
              {compactMode ? 'Detailed' : 'Compact'}
            </button>
            <button onClick={expandAll} className="text-xs text-text-secondary hover:text-text-primary px-2 py-1 rounded hover:bg-surface-800 transition-colors">
              Expand All
            </button>
            <button onClick={collapseAll} className="text-xs text-text-secondary hover:text-text-primary px-2 py-1 rounded hover:bg-surface-800 transition-colors">
              Collapse All
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search conversation..." className="w-full bg-surface-800 border border-surface-500 rounded-lg pl-10 pr-4 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto">
          {[{
          id: 'all',
          label: 'All'
        }, {
          id: 'code',
          label: 'With code'
        }, {
          id: 'errors',
          label: 'Errors'
        }, {
          id: 'artifacts',
          label: 'With artifacts'
        }].map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id as 'all' | 'code' | 'errors' | 'artifacts')} className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${activeFilter === filter.id ? 'bg-primary-500 text-text-primary' : 'bg-surface-800 text-text-secondary hover:text-text-primary'}`}>
              {filter.label}
            </button>)}
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-surface-800" />

          {/* Sections */}
          <div className="space-y-4">
            {filteredSections.map((section) => {
            const isExpanded = expandedSections.has(section.id);
            const promptText = section.userPrompt || section.prompt || 'No prompt';
            const responses = section.agentResponses || [];
            const responseCount = section.responseCount ?? responses.length;
            return <div key={section.id} className="relative pl-10">
                  {/* Timeline Dot */}
                  <div className={`absolute left-2.5 top-3 w-3 h-3 rounded-full border-2 border-surface-700 ${section.status === 'success' ? 'bg-success-500' : section.status === 'error' ? 'bg-danger-500' : 'bg-warning-500 animate-pulse'}`} />

                  {/* Section Header (Collapsed) */}
                  <button onClick={() => toggleSection(section.id)} className="w-full text-left p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 border border-surface-500 transition-colors group">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-text-muted">
                            {section.timestamp}
                          </span>
                          {getStatusIcon(section.status)}
                          {section.hasCode && <Code className="w-3 h-3 text-primary-400" />}
                          {section.hasArtifacts && <FileText className="w-3 h-3 text-purple-400" />}
                        </div>
                        <p className={`text-sm text-text-primary ${compactMode ? 'truncate' : 'line-clamp-2'}`}>
                          {highlightText(promptText)}
                        </p>
                        {!compactMode && !isExpanded && <div className="mt-1 text-xs text-text-muted">
                            {responseCount} response
                            {responseCount !== 1 ? 's' : ''}
                          </div>}
                      </div>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-text-muted flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-text-muted flex-shrink-0" />}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {isExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden">
                        <div className="mt-3 space-y-4 pl-4 border-l-2 border-surface-600">
                          {/* User Prompt */}
                          <div className="flex gap-3">
                            <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                              <User className="w-3 h-3 text-text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="text-xs text-text-muted mb-1">
                                You
                              </div>
                              <div className="bg-primary-600 text-text-primary rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
                                {highlightText(promptText)}
                              </div>
                              <div className="flex gap-2 mt-2">
                                <button className="text-xs text-text-muted hover:text-text-secondary flex items-center gap-1">
                                  <Copy className="w-3 h-3" />
                                  Copy
                                </button>
                                <button className="text-xs text-text-muted hover:text-text-secondary flex items-center gap-1">
                                  <RotateCcw className="w-3 h-3" />
                                  Retry
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Agent Responses */}
                          {responses.map((response) => <div key={response.id} className="flex gap-3">
                              <div className="w-6 h-6 rounded-full bg-surface-700 flex items-center justify-center flex-shrink-0">
                                <Bot className="w-3 h-3 text-primary-400" />
                              </div>
                              <div className="flex-1">
                                <div className="text-xs text-text-muted mb-1">
                                  {response.model}
                                </div>
                                <div className="bg-surface-800 text-text-primary rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm border border-surface-500">
                                  {highlightText(response.content)}
                                </div>

                                {/* Tool Use */}
                                {response.toolUse && response.toolUse.length > 0 && <div className="mt-2 space-y-1">
                                      {response.toolUse.map((tool, i: number) => <div key={i} className="flex items-center gap-2 text-xs text-text-muted bg-surface-900/50 px-2 py-1 rounded">
                                          <span className="text-teal-400">
                                            🔧
                                          </span>
                                          <span>{tool.description}</span>
                                        </div>)}
                                    </div>}

                                {/* Artifacts */}
                                {response.artifacts && response.artifacts.length > 0 && <div className="mt-2 flex gap-2">
                                      {response.artifacts.map((artifact, i: number) => <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded bg-purple-500/10 border border-purple-500/20 text-xs text-purple-400">
                                          <FileText className="w-3 h-3" />
                                          {artifact.name}
                                        </div>)}
                                    </div>}
                              </div>
                            </div>)}

                          {/* Section Actions */}
                          <div className="flex items-center gap-3 pt-2 border-t border-surface-600">
                            <button className="text-xs text-text-muted hover:text-text-secondary">
                              Collapse
                            </button>
                            <button className="text-xs text-text-muted hover:text-text-secondary flex items-center gap-1">
                              <GitBranch className="w-3 h-3" />
                              Branch from here
                            </button>
                            <button className="text-xs text-text-muted hover:text-text-secondary">
                              Export section
                            </button>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}

            {/* No results message */}
            {filteredSections.length === 0 && sections.length > 0 && (
              <div className="text-center py-8 text-text-muted text-sm">
                No results match "{searchQuery || activeFilter}"
              </div>
            )}
          </div>
        </div>
      </div>
    </div>;
}